import { useState } from "react";
import {
  formatTimeOrDash,
  formatWeekdayDateOrDash,
} from "@shared-core/domain/formatters";
import { carrierProgress } from "@shared-core/domain/customerOrderDetail";
import type { OrderParcelEventsState } from "@shared-core/domain/useOrderConsignmentProgress";
import type {
  CustomerConsignmentProgress,
  CustomerParcelEvent,
} from "@shared-core/data/customer-api-types";
import { cn } from "../../../app/components/primitives/utils";
import {
  AlertTriangle,
  Clock,
  Package,
  PackageCheck,
  PackageOpen,
  PackageX,
  Truck,
} from "lucide-react";

// The parcel timeline under the Track with {carrier} button (doc 43): where the parcel is, in our words. Every sentence
// is the backend's (the headline, the note, each label and detail); this draws them, formats the times (UK time, as
// the carrier's depots keep it) and picks an icon per kind. It is a progressive extra: while the carrier is slow or
// down it says so plainly, and the carrier line and public link above it stay whatever it shows.

const KIND_ICON: Record<CustomerParcelEvent["kind"], typeof Truck> = {
  booked: Package,
  collected: PackageOpen,
  on_its_way: Truck,
  out_for_delivery: Truck,
  delivered: PackageCheck,
  delivery_attempted: PackageX,
  problem: AlertTriangle,
};

const KIND_TONE: Partial<Record<CustomerParcelEvent["kind"], string>> = {
  delivered: "text-green-700 dark:text-green-400",
  delivery_attempted: "text-amber-700 dark:text-amber-400",
  problem: "text-red-700 dark:text-red-400",
};

function formatEventTime(occurredAt: string): string {
  return `${formatWeekdayDateOrDash(occurredAt)}, ${formatTimeOrDash(occurredAt)}`;
}

/** The milestones newest first, the newest `foldAfter` of them and the rest behind "Show all N updates". */
function ParcelTimeline({
  events,
  foldAfter,
}: {
  events: CustomerParcelEvent[];
  foldAfter: number | null;
}) {
  const [isUnfolded, setIsUnfolded] = useState(false);
  const isFolded =
    foldAfter !== null && !isUnfolded && events.length > foldAfter;
  const shown = isFolded ? events.slice(0, foldAfter) : events;
  return (
    <>
      <ol className="space-y-2.5">
        {shown.map((event, index) => {
          const Icon = KIND_ICON[event.kind];
          return (
            <li
              key={`${event.kind}-${event.occurred_at}-${index}`}
              className="flex items-start gap-2.5 text-sm"
            >
              <Icon
                className={cn(
                  "mt-0.5 size-4 shrink-0 text-muted-foreground",
                  KIND_TONE[event.kind],
                )}
              />
              <div className="min-w-0 flex-1">
                <p className={cn(index === 0 && "font-medium")}>
                  {event.label}
                </p>
                {event.detail && (
                  <p className="text-muted-foreground">{event.detail}</p>
                )}
              </div>
              <time
                dateTime={event.occurred_at}
                className="shrink-0 text-xs text-muted-foreground tabular-nums"
              >
                {formatEventTime(event.occurred_at)}
              </time>
            </li>
          );
        })}
      </ol>
      {isFolded && (
        <button
          type="button"
          className="text-xs font-medium text-primary underline-offset-2 hover:underline"
          onClick={() => setIsUnfolded(true)}
        >
          Show all {events.length} updates
        </button>
      )}
    </>
  );
}

/**
 * A consignment's milestones as the order read serves them (the Delivery tab on a CIS order): the server decided
 * whether they show and in what words, so there is nothing to fetch, no loading state and no headline here.
 */
export function ServedParcelUpdates({
  progress,
  foldAfter = null,
}: {
  progress: CustomerConsignmentProgress;
  foldAfter?: number | null;
}) {
  if (progress.events.length === 0) return null;
  return (
    <section
      aria-label="Parcel updates"
      className="space-y-3 border-t border-border/60 pt-3"
    >
      <ParcelTimeline events={progress.events} foldAfter={foldAfter} />
    </section>
  );
}

export function OrderParcelEvents({
  state,
  isHeadlineShown = true,
  foldAfter = null,
}: {
  state: OrderParcelEventsState;
  /** False where the parcel's own sentence already stands above (the Delivery tab's day line). */
  isHeadlineShown?: boolean;
  /** Show the newest this many milestones and fold the rest behind "Show all N updates". */
  foldAfter?: number | null;
}) {
  if (state.status === "idle" || state.status === "failed") return null;

  if (state.status === "loading") {
    return (
      <div
        aria-busy="true"
        aria-label="Checking for parcel updates"
        className="space-y-2 border-t border-border/60 pt-3"
      >
        <div className="h-4 w-2/5 animate-pulse rounded bg-muted" />
        <div className="h-3 w-3/5 animate-pulse rounded bg-muted" />
        <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  const { events } = state;
  if (events.state === "not_tracked") return null;

  const newest = events.events[0];
  // An out-for-delivery or delivered parcel's headline is already the card's top line, so the timeline stands alone
  // under it. Any other answer (not scanned yet, can't reach the carrier, a problem) says its own sentence here.
  const showsHeadline = isHeadlineShown && carrierProgress(events) === null;
  const isTroubled =
    events.state === "unavailable" ||
    newest?.kind === "problem" ||
    newest?.kind === "delivery_attempted";
  const HeadlineIcon =
    events.state === "events"
      ? null
      : events.state === "not_yet"
        ? Clock
        : AlertTriangle;

  return (
    <section
      aria-label="Parcel updates"
      className="space-y-3 border-t border-border/60 pt-3"
    >
      {showsHeadline && (
        <div className="space-y-0.5">
          <p
            className={cn(
              "flex items-center gap-2 text-sm font-medium",
              isTroubled && "text-amber-700 dark:text-amber-400",
              newest?.kind === "problem" && "text-red-700 dark:text-red-400",
            )}
          >
            {HeadlineIcon && <HeadlineIcon className="size-4 shrink-0" />}
            {events.headline}
          </p>
          {events.note && (
            <p className="text-sm text-muted-foreground">{events.note}</p>
          )}
        </div>
      )}
      {events.events.length > 0 && (
        <ParcelTimeline events={events.events} foldAfter={foldAfter} />
      )}
      {events.state === "unavailable" && events.as_at && (
        <p className="text-xs text-muted-foreground">
          Last updated {formatEventTime(events.as_at)}
        </p>
      )}
    </section>
  );
}
